import {
  BasketballEvent,
  BasketballEventFilterType,
  BasketballEventReplyAnswer,
  BasketballEventType,
  BasketballEventUserRolePlayerCoach,
  BasketballEventUserRoleType,
  BasketballGame,
  BasketballGameReply,
  BasketballPracticeReply,
  BasketballTeamBasic,
  ResponseBaseUpdateEventReply,
  ResponseBasketballUpdatePracticeNominees,
  ResponseBasketballUpdateTeamGameMeetAt,
  ResponseBasketballUpdateTeamGameNominees,
  UserBasic,
} from '@hefe/models';
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
  isAnyOf,
} from '@reduxjs/toolkit';
import daysjs from 'dayjs';
import _ from 'lodash';
import API from '../../../api/api';
import { BasketballUserEventsRequest } from '../../../models/requests/basketball-event';
import { BasketballGameReplyRequest } from '../../../models/requests/basketball-game';
import { BasketballPracticeReplyRequest } from '../../../models/requests/basketball-practice';
import { eventsApi } from '../../base/events/events.api';
import {
  deleteBasketballGame,
  saveBasketballGame,
} from '../actions/basketball-game/basketball-game.actions';
import { setBasketballPracticeNominees } from '../actions/basketball-practices/basketball-practices.actions';
import {
  setBasketballTeamGameMeetingAt,
  setBasketballTeamGameNominees,
} from '../actions/basketball-team-game/basketball-team-game.actions';

const SLICE_KEY = 'basketballEvents';

interface AddFilterPayload {
  type: BasketballEventFilterType;
  id: number;
}

interface RemoveFilterPayload {
  type: BasketballEventFilterType;
  id: number;
}

export interface BasketballEventsState {
  events: {
    [key: string]: BasketballEvent[];
  };
  filters: {
    types: BasketballEventType[];
    roles: BasketballEventUserRoleType[];
    users: UserBasic[];
    teams: BasketballTeamBasic[];
    tasks: {
      id: number;
      name: string;
    }[];
  };
  activeFilters: {
    types: number[];
    roles: number[];
    users: number[];
    teams: number[];
    tasks: number[];
  };
  pagination: {
    current_first_date?: string;
    current_last_date?: string;
    days_future?: number;
    days_past?: number;
    has_past_events?: boolean;
    overall_first_date?: string;
  };
  currentGoBackDate: daysjs.Dayjs;
}

const initialState: BasketballEventsState = {
  events: {},
  filters: {
    types: [],
    roles: [],
    users: [],
    teams: [],
    tasks: [],
  },
  activeFilters: {
    types: [],
    roles: [],
    users: [],
    teams: [],
    tasks: [],
  },
  pagination: {},
  currentGoBackDate: daysjs(),
};

const GO_BACK_DAYS = 60;

export const fetchUserBasketballEvents = createAsyncThunk(
  'basketballEvents/getAll',
  async (goBack: boolean | undefined, thunkAPI) => {
    let data: BasketballUserEventsRequest | undefined = undefined;

    if (goBack) {
      const date = (thunkAPI.getState() as any).basketballEvents
        .currentGoBackDate;
      data = {
        daysFuture: 0,
        daysPast: GO_BACK_DAYS,
        originDate: date.format('YYYY-MM-DD'),
      };
    }

    const response = await API.basketballUser().getAllEvents(data ?? {});
    return response?.data;
  }
);

export const updateUserBasketballGameReply = createAsyncThunk(
  'basketballEvents/updateUserGameReply',
  async (
    data: BasketballGameReplyRequest,
    thunkAPI
  ): Promise<BasketballGameReply> => {
    const response = await API.basketballGames().updateReply(data);
    return response?.data;
  }
);

export const updateUserBasketballPracticeReply = createAsyncThunk(
  'basketballEvents/updateUserPracticeReply',
  async (
    data: BasketballPracticeReplyRequest,
    thunkAPI
  ): Promise<BasketballPracticeReply> => {
    const response = await API.basketballPractices().updateReply(data);
    return response?.data;
  }
);

const slice = createSlice({
  name: SLICE_KEY,
  initialState: initialState,
  reducers: {
    addFilter: (state, action: PayloadAction<AddFilterPayload>) => {
      state.activeFilters[action.payload.type].push(action.payload.id);
    },
    removeFilter: (state, action: PayloadAction<RemoveFilterPayload>) => {
      state.activeFilters[action.payload.type] = state.activeFilters[
        action.payload.type
      ].filter((id) => id !== action.payload.id);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        fetchUserBasketballEvents.fulfilled,
        (state: BasketballEventsState, action: PayloadAction<any>) => {
          // Set current GoBackDate
          if (action.payload.pagination.days_past === 0) {
            state.currentGoBackDate = daysjs();
          }

          state.currentGoBackDate = state.currentGoBackDate.subtract(
            action.payload.pagination.days_past,
            'days'
          );

          state.events = {
            ...action.payload.events,
            ...state.events,
          };
          state.pagination = action.payload.pagination;

          if (action.payload.pagination.days_past === 0) {
            state.filters = action.payload.filters;
          } else {
            // Merge filters
            state.filters = {
              ...state.filters,
              types: _.uniq([
                ...state.filters.types,
                ...action.payload.filters.types,
              ]),
              roles: _.uniq([
                ...state.filters.roles,
                ...action.payload.filters.roles,
              ]),
              users: _.uniqBy(
                [...state.filters.users, ...action.payload.filters.users],
                'id'
              ),
              teams: _.uniqBy(
                [...state.filters.teams, ...action.payload.filters.teams],
                'id'
              ),
              tasks: _.uniqBy(
                [...state.filters.tasks, ...action.payload.filters.tasks],
                'id'
              ),
            };
          }
        }
      )
      .addCase(
        updateUserBasketballGameReply.fulfilled,
        (
          state: BasketballEventsState,
          action: PayloadAction<BasketballGameReply>
        ) => {
          const reply = action.payload;
          const index = findUserRoleIndex(
            state.events,
            BasketballEventType.GAME,
            reply.game_id,
            BasketballEventUserRoleType.PLAYER,
            reply.repliable_id
          );

          if (index.key === '') return;

          const userRole = state.events[index.key][index.eventIndex]?.user_data[
            index.userIndex
          ]?.roles[index.roleIndex] as BasketballEventUserRolePlayerCoach;

          if (userRole?.reply) {
            userRole.reply.reply = reply?.reply;
          }

          if (state.events[index.key][index.eventIndex]) {
            updateTeamReplies(state.events[index.key][index.eventIndex], reply);
          }
        }
      )
      .addCase(
        updateUserBasketballPracticeReply.fulfilled,
        (
          state: BasketballEventsState,
          action: PayloadAction<BasketballPracticeReply>
        ) => {
          const reply = action.payload;
          const index = findUserRoleIndex(
            state.events,
            BasketballEventType.PRACTICE,
            reply.practice_id,
            BasketballEventUserRoleType.PLAYER,
            reply.repliable_id
          );

          if (index.key === '') return;

          const userRole = state.events[index.key][index.eventIndex]?.user_data[
            index.userIndex
          ]?.roles[index.roleIndex] as BasketballEventUserRolePlayerCoach;

          if (userRole?.reply) {
            userRole.reply.reply = reply?.reply;
          }

          updateTeamReplies(state.events[index.key][index.eventIndex], reply);
        }
      );
    builder.addCase(
      setBasketballTeamGameMeetingAt.fulfilled,
      (
        state,
        action: PayloadAction<ResponseBasketballUpdateTeamGameMeetAt>
      ) => {
        const response = action.payload;
        const index = findEventIndex(
          state.events,
          BasketballEventType.GAME,
          response.game_id
        );

        if (index.key === '') return;

        const game = state.events[index.key][index.index];

        const team = game.teams.find((t) => t.id === response.team_id);
        if (team) {
          team.meet_at = {
            meet_at_time: response.meet_at,
            meet_at_location: response.meet_at_location,
          };
        }
      }
    );
    /***************************************************************
     *                      GAMES
     **************************************************************/
    builder.addCase(
      saveBasketballGame.fulfilled,
      (state, action: PayloadAction<BasketballGame>) => {
        if (action.payload) {
          const game = action.payload;
          const index = findEventIndex(
            state.events,
            BasketballEventType.GAME,
            game.id
          );

          if (index.key === '') return;

          if (game.event) {
            state.events[index.key][index.index] = game.event;
          }
        }
      }
    );
    builder.addCase(
      deleteBasketballGame.fulfilled,
      (state, action: PayloadAction<number>) => {
        if (action.payload && state.events) {
          const index = findEventIndex(
            state.events,
            BasketballEventType.GAME,
            action.payload
          );

          if (index.key === '') return;

          delete state.events[index.key][index.index];
        }
      }
    );
    /***************************************************************
     *                      MATCHERS
     **************************************************************/
    builder.addMatcher(
      isAnyOf(
        setBasketballTeamGameNominees.fulfilled,
        setBasketballPracticeNominees.fulfilled
      ),
      (
        state,
        action: PayloadAction<
          | ResponseBasketballUpdateTeamGameNominees
          | ResponseBasketballUpdatePracticeNominees
        >
      ) => {
        let eventId = 0;
        let eventType: BasketballEventType = BasketballEventType.GAME;

        if (action.type === setBasketballTeamGameNominees.fulfilled.type) {
          eventId = (action.payload as ResponseBasketballUpdateTeamGameNominees)
            .game_id;
          eventType = BasketballEventType.GAME;
        } else {
          eventId = (action.payload as ResponseBasketballUpdatePracticeNominees)
            .practice_id;
          eventType = BasketballEventType.PRACTICE;
        }

        const response = action.payload;
        const index = findEventIndex(state.events, eventType, eventId);

        if (index.key === '') return;

        const game = state.events[index.key][index.index];

        const team = game?.teams.find((t) => t.id === response.team_id);
        if (team) {
          team.replies = {
            yes: response.player_replies.yes.length,
            no: response.player_replies.no.length,
            unknown: response.player_replies.unknown.length,
            not_nominated: response.player_replies.not_nominated.length,
          };
        }

        // update user role
        for (const user of game.user_data) {
          for (const role of user.roles) {
            if (role.type === BasketballEventUserRoleType.PLAYER) {
              (role as BasketballEventUserRolePlayerCoach).nominated =
                response.nominees.includes(user.user.id);
            }
          }
        }
      }
    );
    builder.addMatcher(
      isAnyOf(eventsApi.endpoints.updateEventReply.matchFulfilled),
      (state, action: PayloadAction<ResponseBaseUpdateEventReply>) => {
        const reply = action.payload;
        const index = findUserRoleIndex(
          state.events,
          BasketballEventType.EVENT,
          action.payload.eventable_id,
          BasketballEventUserRoleType.PARTICIPANT,
          reply.repliable_id
        );

        if (index.key === '') return;

        const userRole = state.events[index.key][index.eventIndex]?.user_data[
          index.userIndex
        ]?.roles[index.roleIndex] as BasketballEventUserRolePlayerCoach;

        if (userRole?.reply) {
          userRole.reply.reply = reply?.reply;
        }
      }
    );
    /**
       *  CRUD Practices
       *  TODO Does not work?! dont know why 😭
       *
      .addCase(
        updateBasketballPractices.fulfilled,
        (
          state: BasketballEventsState,
          action: PayloadAction<BasketballEvent[]>
        ) => {
          action.payload.forEach((practice) => {
            const practiceIndex = findEventIndex(
              state.events,
              BasketballEventType.PRACTICE,
              practice.id
            );

            if (practiceIndex.key !== '') {
              state.events[practiceIndex.key][practiceIndex.index] = practice;
            }
          });
        }
      );
      */
  },
});

/*
 * Export reducer for store configuration.
 */
export const basketballEventsReducer = slice.reducer;

export const basketballEventsActions = slice.actions;

const state = (rootState: any): BasketballEventsState => rootState[SLICE_KEY];

export const basketballEventsState = createSelector(state, (state) => state);

export const basketballEventsFiltersSelector = createSelector(
  state,
  (state) => state.filters
);

export const basketballEventsActiveFiltersSelector = createSelector(
  state,
  (state) => state.activeFilters
);

export const filteredBasketballUserEvents = createSelector(state, (state) => {
  let returnValue: any = {};

  const types = state.activeFilters.types;
  const roles = state.activeFilters.roles;
  const teams = state.activeFilters.teams;
  const users = state.activeFilters.users;
  const tasks = state.activeFilters.tasks;

  if (hasActiveFilters(state)) {
    for (const date in state.events) {
      for (const event of state.events[date]) {
        const foundType = types.includes(event.type) || types.length === 0;
        const foundTeam =
          event.teams.map((t) => t.id).some((id) => teams.includes(id)) ||
          teams.length === 0;

        let foundRole = false;
        let foundUser = false;
        let foundTask = false;

        event.user_data.forEach((user) => {
          const isUserEvent = users.includes(user.user.id);
          if (!foundUser) {
            foundUser = isUserEvent;
          }

          if (!foundRole) {
            if (users.length > 0) {
              foundRole =
                isUserEvent &&
                user.roles
                  .map((r) => r.type)
                  .some((type) => roles.includes(type));
            } else {
              foundRole = user.roles
                .map((r) => r.type)
                .some((type) => roles.includes(type));
            }
          }

          if (!foundTask) {
            const userRoleIncludesTaskId = user.roles
              .filter((r) => r.type === BasketballEventUserRoleType.TASK)
              .map((r) => r.task_id ?? 0)
              .some((taskId) => tasks.includes(taskId));
            if (users.length > 0) {
              foundTask = isUserEvent && userRoleIncludesTaskId;
            } else {
              foundTask = userRoleIncludesTaskId;
            }
          }
        });

        foundRole = foundRole || roles.length === 0;
        foundUser = foundUser || users.length === 0;
        foundTask = foundTask || tasks.length === 0;

        if (foundType && foundTeam && foundRole && foundUser && foundTask) {
          if (!returnValue[date]) {
            returnValue[date] = [] as BasketballEvent[];
          }

          returnValue[date].push(event);
        }
      }
    }
  } else {
    returnValue = state.events;
  }

  return returnValue;
});

export const isBasketballUserEventsFilterActive = createSelector(
  state,
  (state) => {
    return hasActiveFilters(state);
  }
);

export const numberOfActiveBasketballUserEventsFilters = createSelector(
  state,
  (state) => {
    return numberOfActiveFilters(state);
  }
);

export const basketballUserEventsPaginationSelector = createSelector(
  state,
  (state) => {
    return state.pagination;
  }
);

export const basketballUserEventsCurrentGoBackDateSelector = createSelector(
  state,
  (state) => {
    return state.currentGoBackDate;
  }
);

export const basketballUserEventsNextGoBackDateSelector = createSelector(
  state,
  (state) => {
    return state.currentGoBackDate.subtract(GO_BACK_DAYS, 'days');
  }
);

/**
 *
 *
 *  Helper function
 *
 *
 */
export const findEventIndex = (
  events: { [key: string]: BasketballEvent[] },
  type: BasketballEventType,
  id: number
): {
  key: string;
  index: number;
} => {
  let returnValue = {
    key: '',
    index: 0,
  };

  for (const key in events) {
    const eventsOfDay = events[key];
    const indexOfEvent = eventsOfDay.findIndex(
      (event) => event.type === type && event.id === id
    );
    if (indexOfEvent > -1) {
      returnValue = {
        key: key,
        index: indexOfEvent,
      };
    }
  }

  return returnValue;
};

export const findUserRoleIndex = (
  events: { [key: string]: BasketballEvent[] },
  eventType: BasketballEventType,
  id: number,
  roleType: BasketballEventUserRoleType,
  userId?: number
): {
  key: string;
  eventIndex: number;
  userIndex: number;
  roleIndex: number;
} => {
  const eventIndex = findEventIndex(events, eventType, id);

  if (eventIndex.key === '') {
    return {
      key: '',
      eventIndex: -1,
      userIndex: -1,
      roleIndex: -1,
    };
  }

  const userIndex = events[eventIndex.key][
    eventIndex.index
  ]?.user_data?.findIndex((user) => user.user.id === userId);

  let roleIndex = -1;
  if (userIndex > -1) {
    roleIndex = events[eventIndex.key][eventIndex.index]?.user_data[
      userIndex
    ]?.roles?.findIndex((role) => role.type === roleType);
  }

  return {
    key: eventIndex.key,
    eventIndex: eventIndex.index,
    userIndex: userIndex,
    roleIndex: roleIndex,
  };
};

export const updateTeamReplies = (
  event: BasketballEvent,
  reply: BasketballGameReply | BasketballPracticeReply
): BasketballEvent => {
  const subtractYes =
    reply.old_reply === BasketballEventReplyAnswer.COMMITMENT ? -1 : 0;
  const subtractNo =
    reply.old_reply === BasketballEventReplyAnswer.CANCELED ? -1 : 0;
  const subtractUnknown = subtractYes === 0 && subtractNo === 0 ? -1 : 0;

  const addYes = reply?.reply === BasketballEventReplyAnswer.COMMITMENT ? 1 : 0;
  const addNo = reply?.reply === BasketballEventReplyAnswer.CANCELED ? 1 : 0;

  const team = event.teams.find((team) => team.id === reply.team_id);
  if (team) {
    if (team.replies) {
      team.replies = {
        yes: team.replies.yes + subtractYes + addYes,
        no: team.replies.no + subtractNo + addNo,
        unknown: team.replies.unknown + subtractUnknown,
        not_nominated: team.replies.not_nominated,
      };
    }
  }

  return event;
};

/**
 *
 *  Private functions
 *
 */
const numberOfActiveFilters = (state: BasketballEventsState): number => {
  let number = 0;
  for (const filter of _.values(state.activeFilters)) {
    number += filter.length;
  }

  return number;
};
const hasActiveFilters = (state: BasketballEventsState): boolean => {
  return numberOfActiveFilters(state) > 0;
};
